/**
 * Action Plan Service
 * API client for managing action plan tasks
 */

import { API_BASE_URL } from './api';

/**
 * Build standard request headers including Authorization: Bearer <jwt>
 * matching the platform-wide auth convention (token stored as 'access_token').
 */
function getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
}

export interface ActionPlanTaskPage {
    id: number;
    page_url: string;
    issue_description?: string;
    current_value?: string;
    recommended_value?: string;
}

export interface ActionPlanTask {
    id: number;
    title: string;
    description?: string;
    category: 'onpage' | 'offpage' | 'technical';
    priority: 'high' | 'medium' | 'low';
    status: 'not_started' | 'in_progress' | 'completed';
    impact_score: number;
    effort_score: number;
    parameter_group?: string;
    sub_parameter?: string;
    related_goal?: string;
    affected_pages_count: number;
    impact_description?: string;
    effort_description?: 'Low' | 'Medium' | 'High';
    recommendation?: string;
    created_at?: string;
    updated_at?: string;
    completed_at?: string;
    // SEOptimer-style per-issue details for the View Details modal
    pages?: ActionPlanTaskPage[];
}

// ─── View Details Modal types ─────────────────────────────────────────────────

export interface TaskDetailsTableRow {
    [column: string]: string | number;
}

export interface TaskDetailsSummary {
    [label: string]: number | string;
}

export type TaskDetailsResponse =
    | {
          detail_type: 'table';
          title: string;
          description: string;
          columns: string[];
          rows: TaskDetailsTableRow[];
          summary?: TaskDetailsSummary;
          task_id: number;
          task_title: string;
          category: string;
          sub_parameter?: string;
      }
    | {
          detail_type: 'action_item';
          title: string;
          text: string;
          task_id: number;
          task_title: string;
          category: string;
          sub_parameter?: string;
      }
    | {
          detail_type: 'on_page';
          task_id: number;
          task_title: string;
          category: string;
          sub_parameter?: string;
      };


export interface ActionPlanSummary {
    total_tasks: number;
    not_started: number;
    in_progress: number;
    completed: number;
}

export interface ActionPlanResponse {
    status: string;
    message: string;
    user_id: string;
    data: {
        tasks?: ActionPlanTask[];
        count?: number;
        tasks_generated?: {
            onpage: number;
            offpage: number;
            technical: number;
            total: number;
        };
    } | ActionPlanSummary;
}

/**
 * Generate action plan from As-Is state data
 */
export const generateActionPlan = async (userId: string): Promise<ActionPlanResponse> => {
    const response = await fetch(`${API_BASE_URL}/action-plan/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(error.detail || 'Failed to generate action plan');
    }

    return response.json();
};

/**
 * Get action plan tasks with optional filters
 */
export const getActionPlanTasks = async (
    userId: string,
    filters?: {
        category?: 'onpage' | 'offpage' | 'technical';
        priority?: 'high' | 'medium' | 'low';
        status?: 'not_started' | 'in_progress' | 'completed';
    }
): Promise<ActionPlanTask[]> => {
    const params = new URLSearchParams({ ...filters });
    const url = `${API_BASE_URL}/action-plan/${userId}?${params.toString()}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(error.detail || 'Failed to fetch action plan tasks');
    }

    const data: ActionPlanResponse = await response.json();
    return (data.data as { tasks: ActionPlanTask[] }).tasks || [];
};

/**
 * Get action plan summary (task counts)
 */
export const getActionPlanSummary = async (userId: string): Promise<ActionPlanSummary> => {
    const response = await fetch(`${API_BASE_URL}/action-plan/${userId}/summary`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(error.detail || 'Failed to fetch action plan summary');
    }

    const data: ActionPlanResponse = await response.json();
    return data.data as ActionPlanSummary;
};

/**
 * Update task status
 */
export const updateTaskStatus = async (
    taskId: number,
    userId: string,
    newStatus: 'not_started' | 'in_progress' | 'completed',
    notes?: string
): Promise<ActionPlanTask> => {
    const response = await fetch(`${API_BASE_URL}/action-plan/task/${taskId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            user_id: userId,
            new_status: newStatus,
            notes,
        }),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(error.detail || 'Failed to update task status');
    }

    const data: ActionPlanResponse = await response.json();
    return (data.data as { task: ActionPlanTask }).task;
};

/**
 * Get tasks by category
 */
export const getTasksByCategory = async (
    userId: string,
    category: 'onpage' | 'offpage' | 'technical'
): Promise<ActionPlanTask[]> => {
    return getActionPlanTasks(userId, { category });
};

/**
 * Get tasks by priority
 */
export const getTasksByPriority = async (
    userId: string,
    priority: 'high' | 'medium' | 'low'
): Promise<ActionPlanTask[]> => {
    return getActionPlanTasks(userId, { priority });
};

/**
 * Fetch View Details for a specific task.
 * Returns a table of live data (DataForSEO / PSI / GSC) or a 1-2 liner action item.
 */
export const getTaskDetails = async (
    userId: string,
    taskId: number
): Promise<TaskDetailsResponse> => {
    const url = `${API_BASE_URL}/action-plan/${userId}/task/${taskId}/details`;
    const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: `HTTP ${response.status}: ${response.statusText}` }));
        throw new Error(error.detail || 'Failed to fetch task details');
    }

    const data = await response.json();
    return data.data as TaskDetailsResponse;
};
